import {
  CheckCircleIcon,
  FolderSimpleIcon,
  ShoppingBagIcon,
  Sparkle as SparkleIcon,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';

import { useSettingsDialog } from '@/components/settings/SettingsDialogProvider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty';
import { ProductIllustration } from '@/components/ui/illustrations';
import { SearchBar } from '@/components/ui/search-bar';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSafeNavigate } from '@/hooks/useSafeRouter';
import { useShopifyProducts } from '@/hooks/useShopifyProducts';
import { EMPTY_STATES } from '@/lib/empty-state-configs';
import { ITEM_META, ITEM_TITLE, SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { describeConnectionState } from '@/services/socialAuth/connection-state';
import type { ShopifyProductDB } from '@/types/product';
import { parsePrice } from '@/types/product';
import { logger } from '@/utils/logger';

function variantPriceRange(product: ShopifyProductDB): string | undefined {
  const variants = Array.isArray(product.variants)
    ? (product.variants as Array<{ price?: string | number }>)
    : [];
  const prices = variants
    .map((v) => (v?.price != null ? Number(v.price) : NaN))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (prices.length === 0) {
    const fallback = product.min_price != null ? Number(product.min_price) : NaN;
    return Number.isFinite(fallback) && fallback > 0 ? fallback.toFixed(2) : undefined;
  }
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? min.toFixed(2) : `${min.toFixed(2)}-${max.toFixed(2)}`;
}

function computeRelevanceScore(product: ShopifyProductDB): number {
  let score = 0;

  const updatedAt = new Date(product.updated_at || product.created_at);
  const daysSinceUpdate = (Date.now() - updatedAt.getTime()) / (1000 * 60 * 60 * 24);
  score += Math.max(0, 100 - daysSinceUpdate * 3);

  if ((product.total_inventory ?? 0) > 0) score += 50;

  const price = parsePrice(product.min_price);
  if (price > 0 && price < 100) score += 20;
  else if (price >= 100 && price < 300) score += 10;

  return score;
}

interface ProductPickerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProduct: (product: {
    id: string;
    title: string;
    imageUrl?: string;
    type?: 'product' | 'collection';
    productPrice?: string;
    productKind?: string;
  }) => void;
  selectedProductIds: string[];
  onGenerateForAll?: () => void;
  /** Project context supports products only; collection references have no
   * durable model/lineage contract yet. */
  allowCollections?: boolean;
}

export function ProductPickerSheet({
  isOpen,
  onClose,
  onSelectProduct,
  selectedProductIds,
  onGenerateForAll,
  allowCollections = true,
}: ProductPickerSheetProps) {
  const _navigate = useSafeNavigate();
  void _navigate;
  const { openSettings } = useSettingsDialog();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'products' | 'collections'>('products');
  const [collections, setCollections] = useState<any[]>([]);
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  const {
    products,
    isLoading: isProductsLoading,
    error,
    connection,
    hasConnection,
    connectionState,
    searchProducts,
    searchShopifyCollections,
    saveProductToCache,
    syncProducts,
    isSyncing,
    refetch,
  } = useShopifyProducts({ searchQuery: search });

  const isLoading = activeTab === 'products' ? isProductsLoading : isLoadingCollections;

  // "Connect Shopify" is only true when nothing was ever linked. A store whose
  // token lapsed is a DIFFERENT situation with a different fix — and its
  // last-synced products are still here. Telling that merchant to connect a
  // store they already connected is the J4 dead end.
  const connectionCopy = describeConnectionState(connectionState, {
    platformLabel: 'Shopify',
    shopDomain: connection?.shop_domain,
    cachedItemCount: products.length,
  });

  // Browsing and selecting work off data we already hold, so a lapsed token
  // must not hide them. Only affordances that need a LIVE call (sync) stay
  // gated on `hasConnection`.
  const canBrowseProducts = hasConnection || products.length > 0;

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (activeTab === 'products') {
        searchProducts(search);
      } else {
        setIsLoadingCollections(true);
        searchShopifyCollections(search)
          .then((results) => {
            setCollections(results);
            setIsLoadingCollections(false);
          })
          .catch((err) => {
            logger.error('Failed to fetch collections', err);
            setIsLoadingCollections(false);
          });
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [search, searchProducts, activeTab, searchShopifyCollections]);

  const { recommended, rest } = useMemo(() => {
    if (products.length < 4) return { recommended: [], rest: products };
    const scored = products
      .map((p) => ({ product: p, score: computeRelevanceScore(p) }))
      .sort((a, b) => b.score - a.score);
    return {
      recommended: scored.slice(0, 3).map((s) => s.product),
      rest: scored.slice(3).map((s) => s.product),
    };
  }, [products]);

  const handleSelectProduct = async (product: ShopifyProductDB) => {
    let selectedId = product.id;
    if (selectedId.startsWith('gid://')) {
      try {
        const saved = (await saveProductToCache(selectedId)) as { product?: { id: string } };
        if (saved?.product?.id) {
          selectedId = saved.product.id;
        }
      } catch (err) {
        logger.error('Failed to save product to cache', err);
      }
    }

    onSelectProduct({
      id: selectedId,
      title: product.title,
      imageUrl: product.images?.[0]?.url,
      type: 'product',
      productPrice: variantPriceRange(product),
      productKind: product.product_type || undefined,
    });
  };

  const handleSelectCollection = (collection: any) => {
    onSelectProduct({
      id: collection.id,
      title: collection.title,
      imageUrl: collection.image?.url,
      type: 'collection',
    });
  };

  const formatPrice = (price: number | string | null) => {
    if (price === null) return null;
    return `$${Number(price).toFixed(2)}`;
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setSearch('');
          onClose();
        }
      }}
    >
      <DialogContent
        size="xl"
        className="flex max-h-[85vh] flex-col gap-0 overflow-hidden rounded-xl border border-border/60 bg-background p-0 shadow-elevation-3"
      >
        <DialogHeader className="px-5 py-3.5 border-b border-border/50 shrink-0">
          <DialogTitle>Add products</DialogTitle>
          <DialogDescription className="sr-only">
            Select {allowCollections ? 'products or collections' : 'products'} from your Shopify
            store.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'products' | 'collections')}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="px-5 pt-3 shrink-0">
            <TabsList className="w-full bg-transparent border-b border-border rounded-none h-auto p-0">
              <TabsTrigger
                value="products"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 pb-2 text-caption text-muted-foreground"
              >
                Products
              </TabsTrigger>
              {allowCollections && (
                <TabsTrigger
                  value="collections"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 pb-2 text-caption text-muted-foreground"
                >
                  Collections
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          {canBrowseProducts && (
            <div className="px-5 py-3 border-b border-border/40 shrink-0">
              <SearchBar
                value={search}
                onChange={setSearch}
                onClear={() => setSearch('')}
                placeholder={`Search ${activeTab}...`}
                className="w-full"
                size="sm"
                variant="neutral"
              />
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-5 py-5">
            {!hasConnection && products.length === 0 ? (
              <EmptyState
                {...EMPTY_STATES['products']}
                illustration={ProductIllustration}
                title={connectionCopy.title}
                description={connectionCopy.description}
                actions={[
                  {
                    label: connectionCopy.actionLabel,
                    onClick: () => {
                      onClose();
                      openSettings('integrations');
                    },
                    variant: 'outline',
                  },
                ]}
              />
            ) : isLoading ? (
              <div className="grid grid-cols-2 gap-3 animate-fade-in">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i}>
                    <div className="aspect-square w-full rounded-xl bg-muted/40 animate-pulse" />
                    <div className="mt-3 h-3.5 w-3/4 rounded bg-muted/40 animate-pulse" />
                    <div className="mt-1.5 h-3 w-1/2 rounded bg-muted/40 animate-pulse" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <EmptyState
                variant="error"
                title={`Couldn't load ${activeTab}`}
                description={error}
                retry={activeTab === 'products' ? refetch : undefined}
              />
            ) : activeTab === 'collections' ? (
              collections.length === 0 ? (
                <EmptyState
                  illustration={ProductIllustration}
                  title="No collections found"
                  description={
                    search
                      ? `No collections matching "${search}"`
                      : 'Sync your Shopify store to see collections here'
                  }
                />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {collections.map((collection) => (
                    <PickerCard
                      key={collection.id}
                      imageUrl={collection.image?.url}
                      title={collection.title}
                      isSelected={selectedProductIds.includes(collection.id)}
                      onClick={() => handleSelectCollection(collection)}
                      fallbackIcon={FolderSimpleIcon}
                    />
                  ))}
                </div>
              )
            ) : products.length === 0 ? (
              <EmptyState
                {...EMPTY_STATES['products']}
                illustration={ProductIllustration}
                title={search ? 'No matching products' : 'No products synced yet'}
                description={
                  search
                    ? `No products matching "${search}"`
                    : 'Sync your Shopify store to load products here.'
                }
                actions={
                  search
                    ? undefined
                    : [
                        {
                          label: isSyncing ? 'Syncing...' : 'Sync products',
                          onClick: () => void syncProducts(),
                          variant: 'outline',
                        },
                      ]
                }
              />
            ) : (
              <div className="space-y-6">
                {recommended.length > 0 && (
                  <div>
                    <h3 className={cn(SECTION_LABEL, 'mb-3')}>Recommended</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {recommended.map((product) => (
                        <PickerCard
                          key={product.id}
                          imageUrl={product.images?.[0]?.url}
                          title={product.title}
                          subtitle={
                            product.min_price !== null ? formatPrice(product.min_price) : null
                          }
                          isSelected={selectedProductIds.includes(product.id)}
                          onClick={() => handleSelectProduct(product)}
                          fallbackIcon={ShoppingBagIcon}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  {recommended.length > 0 && (
                    <h3 className={cn(SECTION_LABEL, 'mb-3')}>All products</h3>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {(recommended.length > 0 ? rest : products).map((product) => (
                      <PickerCard
                        key={product.id}
                        imageUrl={product.images?.[0]?.url}
                        title={product.title}
                        subtitle={
                          product.min_price !== null ? formatPrice(product.min_price) : null
                        }
                        isSelected={selectedProductIds.includes(product.id)}
                        onClick={() => handleSelectProduct(product)}
                        fallbackIcon={ShoppingBagIcon}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            {products.length === 0 && search && !isLoading && activeTab === 'products' && (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">
                  No products found for &quot;{search}&quot;
                </p>
              </div>
            )}
          </div>
        </Tabs>
        {canBrowseProducts && selectedProductIds.length > 0 && (
          <div className="shrink-0 border-t border-border/50 px-6 py-3 bg-background flex flex-col gap-2">
            {onGenerateForAll && selectedProductIds.length >= 2 && (
              <Button
                variant="neutral"
                size="sm"
                className="w-full"
                onClick={() => {
                  setSearch('');
                  onGenerateForAll();
                }}
              >
                <SparkleIcon className="size-3.5" />
                Generate posts for all {selectedProductIds.length} products
              </Button>
            )}
            <Button
              variant={onGenerateForAll && selectedProductIds.length >= 2 ? 'outline' : 'neutral'}
              size="sm"
              className="w-full"
              onClick={() => {
                setSearch('');
                onClose();
              }}
            >
              Done ({selectedProductIds.length} selected)
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Shared picker card for BOTH products and collections — one implementation so
 * the two grids can't drift (selected ring, checkmark, fallback, image crop).
 * `subtitle` carries the price for products; collections omit it. `fallbackIcon`
 * differs (bag vs folder). Same photo-forward, unboxed archetype as the
 * `/products` page's own `ProductCard` (image in its own rounded frame,
 * title/meta on the page background below) — a bordered box was the one card
 * style in this dialog that didn't match its own sibling surface.
 */
function PickerCard({
  imageUrl,
  title,
  subtitle,
  isSelected,
  onClick,
  fallbackIcon: Fallback,
}: {
  imageUrl?: string | null;
  title: string;
  subtitle?: string | null;
  isSelected: boolean;
  onClick: () => void;
  fallbackIcon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <button type="button" onClick={onClick} className="group text-left">
      <div
        className={cn(
          'relative aspect-square w-full overflow-hidden rounded-xl bg-muted/40 ring-2 ring-offset-2 ring-offset-background transition',
          isSelected ? 'ring-primary' : 'ring-transparent',
        )}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            loading="lazy"
            className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-muted/40">
            <Fallback className="size-8 text-muted-foreground/50" />
          </div>
        )}
        {isSelected && (
          <div className="absolute top-2 right-2 rounded-full bg-primary p-0.5 text-primary-foreground">
            <CheckCircleIcon weight="fill" className="size-4" />
          </div>
        )}
      </div>
      <div className="mt-3 px-0.5">
        <p className={cn(ITEM_TITLE, 'truncate')}>{title}</p>
        {subtitle ? <p className={ITEM_META}>{subtitle}</p> : null}
      </div>
    </button>
  );
}
